import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import {
  usePlay,
  usePlayMutations,
  useRunAgent,
  useRefinePlay,
  useReplaceSteps,
  type PlayDetail,
  type StepRow,
  type StepEventRow,
  type ReplanRow,
  type ChatTurn,
  type ProposedStep,
  type RefineResult,
} from "../../hooks/usePlay";
import { usePersona, type Persona } from "../../hooks/usePersona";
import { OutcomeHeader } from "../../components/OutcomeHeader";
import { useMe } from "../../hooks/useMe";
import {
  useWorkspaceEvents,
  type WorkspaceEvent,
} from "../../hooks/useWorkspaceEvents";
import { usePlannerStore } from "../../state/usePlannerStore";
import { projectDraft } from "./planner/projector";
import { StepDetail } from "../../components/StepDetail";
import { ReplanCard } from "../../components/StepDetail/ReplanCard";
import { RequestReplanModal } from "../../components/StepDetail/RequestReplanModal";
import { AgentMatchModal } from "../../components/AgentMatchModal";
import { GovernancePanel } from "../../components/GovernancePanel";
import { PlayRecurrenceModal } from "../../components/PlayRecurrenceModal";
import { MarkdownText, hasMarkdown } from "../../components/review/MarkdownText";

type Tab = "steps" | "timeline" | "outcome" | "governance" | "refine";

function statusPill(state: string): string {
  if (state === "running") return "green";
  if (state === "paused") return "amber";
  if (state === "replanning") return "purple";
  if (state === "completed") return "blue";
  if (state === "abandoned") return "red";
  return "gray";
}


function stepTypeIcon(type: string): string {
  switch (type) {
    case "decision":
      return "◆";
    case "comm":
      return "✉";
    case "query":
      return "?";
    case "milestone":
      return "◇";
    case "wait":
      return "⌛";
    case "instrumentation":
      return "⟳";
    case "micro_task":
    default:
      return "◦";
  }
}

function elapsedLabel(p: PlayDetail["play"]): string {
  if (!p.startedAt) return "—";
  const ms = Date.now() - new Date(p.startedAt).getTime();
  if (ms < 0) return "—";
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 48) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `${days}d`;
}

function windowLabel(p: PlayDetail["play"]): string {
  if (!p.targetWindowEnd) return "—";
  try {
    return new Date(p.targetWindowEnd).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  } catch {
    return p.targetWindowEnd;
  }
}

interface ConfirmActionState {
  kind: "approve" | "start" | "pause" | "resume" | "abandon";
  needsRationale: boolean;
}

function readPendingReplan(detail: PlayDetail): ReplanRow | null {
  // Plan 5 doesn't ship replan rows alongside the play — discover via events.
  // The dashboard recognises the play.state === "replanning"; we surface a
  // placeholder card with the rationale from the most recent replan event.
  if (detail.play.state !== "replanning") return null;
  const evt = detail.recentEvents.find(
    (e) =>
      e.eventType === "replan.requested" || e.eventType === "replan.created",
  );
  const replanIdRaw = evt
    ? (evt.payload as Record<string, unknown>)["replanId"]
    : undefined;
  const triggerKind = evt
    ? ((evt.payload as Record<string, unknown>)["triggerKind"] as string) ?? "cxo_directed"
    : "cxo_directed";
  const rationale = evt
    ? ((evt.payload as Record<string, unknown>)["rationale"] as string) ?? ""
    : "Replan in progress.";
  return {
    id: typeof replanIdRaw === "string" ? replanIdRaw : `pending-${detail.play.id}`,
    playId: detail.play.id,
    state: "awaiting_signoff",
    triggerKind,
    rationale,
  };
}

interface ActivityItem {
  id: string;
  time: string;
  who: string;
  text: string;
}

function eventToActivity(e: WorkspaceEvent, idx: number): ActivityItem {
  const p = (e.payload ?? {}) as Record<string, unknown>;
  const who =
    (typeof p["actorName"] === "string" && p["actorName"]) ||
    (typeof p["actorKind"] === "string" && p["actorKind"]) ||
    "System";
  const subject =
    (typeof p["title"] === "string" && p["title"]) ||
    (typeof p["stepId"] === "string" && `step ${p["stepId"]}`) ||
    "";
  return {
    id: `evt-${Date.now()}-${idx}`,
    time: new Date().toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    }),
    who: String(who),
    text: `${e.type} ${subject}`,
  };
}

interface CxoActionsProps {
  state: string;
  persona: Persona | null;
  onApprove: () => void;
  onStart: () => void;
  onPause: () => void;
  onResume: () => void;
  onAbandon: () => void;
  onRequestReplan: () => void;
  onEdit: () => void;
  onMakeRecurring: () => void;
  hasRoadmapEntry: boolean;
  pending: boolean;
}

function CxoActions({
  state,
  persona,
  onApprove,
  onStart,
  onPause,
  onResume,
  onAbandon,
  onRequestReplan,
  onEdit,
  onMakeRecurring,
  hasRoadmapEntry,
  pending,
}: CxoActionsProps) {
  if (persona !== "cxo" && persona !== "owner") return null;
  return (
    <div
      style={{
        display: "flex",
        gap: 6,
        alignItems: "center",
        flexWrap: "wrap",
      }}
      data-testid="cxo-actions"
    >
      {state === "draft" ? (
        <>
          <button
            type="button"
            className="btn primary"
            onClick={onApprove}
            disabled={pending}
            data-testid="cxo-approve-btn"
            data-tip="Lock in this plan and hand it to the execution team"
          >
            Approve
          </button>
          <button
            type="button"
            className="btn"
            onClick={onEdit}
            disabled={pending}
            data-testid="cxo-edit-btn"
            data-tip="Open the planner editor to revise steps before approving. Or use the Refine tab to chat with XAMI."
          >
            Edit
          </button>
        </>
      ) : null}
      {state === "approved" ? (
        <button
          type="button"
          className="btn primary"
          onClick={onStart}
          disabled={pending}
          data-testid="cxo-start-btn"
          data-tip="Kick off execution — agents and operators are notified and steps become claimable"
        >
          Start
        </button>
      ) : null}
      {state === "running" ? (
        <button
          type="button"
          className="btn"
          onClick={onPause}
          disabled={pending}
          data-testid="cxo-pause-btn"
          data-tip="Pause all execution — no new steps will be dispatched until you resume"
        >
          Pause
        </button>
      ) : null}
      {state === "paused" ? (
        <button
          type="button"
          className="btn primary"
          onClick={onResume}
          disabled={pending}
          data-testid="cxo-resume-btn"
          data-tip="Resume execution from where it left off"
        >
          Resume
        </button>
      ) : null}
      {state !== "completed" && state !== "abandoned" ? (
        <>
          <button
            type="button"
            className="btn"
            onClick={onRequestReplan}
            disabled={pending}
            data-testid="cxo-request-replan-btn"
            data-tip="Ask XAMI to propose a revised plan based on what's changed — blockers, new info, or stakeholder feedback"
          >
            Request replan
          </button>
          <button
            type="button"
            className="btn"
            onClick={onAbandon}
            disabled={pending}
            data-testid="cxo-abandon-btn"
            data-tip="Cancel this play permanently. The initiative returns to the inbox so it can be re-planned later."
          >
            Abandon
          </button>
        </>
      ) : null}
      {/* Make recurring is available across all non-terminal states.
          Wires to PlayRecurrenceModal — that modal handles both cases:
          (a) play has a roadmap entry → PATCHes recurrence on it
          (b) play has no entry → calls /promote-and-recur to create one
          and set the schedule in a single mutation. We unconditionally
          enable the button so the user gets feedback either way. */}
      {(state === "draft" || state === "approved" || state === "running" || state === "paused") ? (
        <button
          type="button"
          className="btn"
          onClick={onMakeRecurring}
          disabled={pending}
          data-testid="cxo-make-recurring-btn"
          data-tip={
            hasRoadmapEntry
              ? "Mark the roadmap entry behind this play as recurring so future cycles spawn their own plays."
              : "This play has no roadmap entry yet — making it recurring will create one for the schedule to attach to."
          }
        >
          ↻ Make recurring
        </button>
      ) : null}
    </div>
  );
}

interface TimelineNode {
  key: string;
  at: number;
  label: string;
  owner: string | null;
  isAutonomous: boolean;
  date: string;
  done: boolean;
  major: boolean;
  kind: "step" | "replan";
}

const tlDate = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
});

function buildTimelineNodes(detail: PlayDetail): TimelineNode[] {
  const { play, steps, recentEvents } = detail;
  const total = steps.length;
  if (total === 0) return [];

  const startMs = play.startedAt
    ? new Date(play.startedAt).getTime()
    : new Date(play.createdAt).getTime();
  const endMs = play.targetWindowEnd
    ? new Date(play.targetWindowEnd).getTime()
    : startMs + total * 86_400_000;
  const span = Math.max(endMs - startMs, 1);

  const placeAt = (s: StepRow): number => {
    const ts = s.completedAt ?? s.startedAt;
    if (ts) {
      const v = (new Date(ts).getTime() - startMs) / span;
      return Math.max(0, Math.min(1, v));
    }
    if (total === 1) return 0;
    return (s.ord - 1) / Math.max(total - 1, 1);
  };

  const stepNodes: TimelineNode[] = steps.map((s) => {
    const t = s.completedAt ?? s.startedAt ?? null;
    return {
      key: `step-${s.id}`,
      at: placeAt(s),
      label: s.title,
      owner: s.displayOwner,
      isAutonomous: s.isAutonomous,
      date: t ? tlDate.format(new Date(t)) : "—",
      done: s.state === "completed",
      major: s.type === "milestone",
      kind: "step",
    };
  });

  const replanNodes: TimelineNode[] = recentEvents
    .filter(
      (e) =>
        e.eventType === "replan.applied" || e.eventType === "replan.requested",
    )
    .map((e, i) => {
      const t = new Date(e.occurredAt).getTime();
      return {
        key: `replan-${e.id}`,
        at: Math.max(0, Math.min(1, (t - startMs) / span)),
        label: `Replan #${i + 1}`,
        owner: null,
        isAutonomous: false,
        date: tlDate.format(new Date(e.occurredAt)),
        done: e.eventType === "replan.applied",
        major: false,
        kind: "replan",
      };
    });

  return [...stepNodes, ...replanNodes].sort((a, b) => a.at - b.at);
}

function PlayTimeline({ detail }: { detail: PlayDetail }) {
  const nodes = useMemo(() => buildTimelineNodes(detail), [detail]);
  const stepsTotal = detail.steps.length;
  const stepsDone = detail.steps.filter((s) => s.state === "completed").length;
  const progress = stepsTotal > 0 ? stepsDone / stepsTotal : 0;

  if (nodes.length === 0) {
    return (
      <div
        className="play-timeline-empty"
        data-testid="play-timeline-empty"
        style={{ padding: 24, fontSize: 13, color: "var(--text-3)", textAlign: "center" }}
      >
        No steps yet — once the play has a step graph, this view shows the milestone strip.
      </div>
    );
  }

  return (
    <div className="play-tl-wrap" style={{ padding: "16px 8px" }}>
      <div
        className="play-tl-rail"
        style={{
          position: "relative",
          height: 64,
          display: "flex",
          alignItems: "center",
          borderTop: "1px solid var(--border)",
          borderBottom: "1px solid var(--border)",
          padding: "0 8px",
          background: "var(--bg)",
        }}
      >
        <div
          style={{
            position: "absolute",
            left: 20,
            right: 20,
            top: "50%",
            height: 2,
            background: "var(--border)",
          }}
        />
        <div
          style={{
            position: "absolute",
            left: 20,
            top: "50%",
            height: 2,
            background: "var(--accent)",
            width: `calc((100% - 40px) * ${progress})`,
            transition: "width 240ms ease",
          }}
        />
        {nodes.map((n) => {
          const isReplan = n.kind === "replan";
          const size = n.major ? 14 : 10;
          return (
            <div
              key={n.key}
              title={n.label}
              style={{
                position: "absolute",
                left: `calc(20px + (100% - 40px) * ${n.at})`,
                top: "50%",
                transform: "translate(-50%, -50%)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                zIndex: 1,
              }}
            >
              <div
                style={{
                  width: size,
                  height: size,
                  borderRadius: "50%",
                  background: isReplan
                    ? "var(--purple)"
                    : n.done
                      ? "var(--accent)"
                      : "white",
                  border: isReplan
                    ? "2px solid var(--purple)"
                    : n.done
                      ? "none"
                      : "2px solid var(--border-strong)",
                  color: "white",
                  fontSize: 8,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {isReplan ? "⟲" : ""}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ position: "relative", height: 60, marginTop: 4 }}>
        {nodes.map((n) => (
          <div
            key={`l-${n.key}`}
            style={{
              position: "absolute",
              left: `calc(20px + (100% - 40px) * ${n.at})`,
              transform: "translateX(-50%)",
              textAlign: "center",
              maxWidth: 130,
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 500,
                color:
                  n.kind === "replan" ? "var(--purple)" : "var(--text)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: 130,
                display: "inline-flex",
                alignItems: "center",
                gap: 3,
                justifyContent: "center",
              }}
            >
              {n.isAutonomous ? (
                <span
                  title="Autonomous — runs unattended"
                  style={{ color: "var(--accent)", fontSize: 10 }}
                  aria-label="autonomous"
                >
                  ⚡
                </span>
              ) : null}
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                {n.label}
              </span>
            </div>
            {n.owner ? (
              <div
                style={{
                  fontSize: 10,
                  color: "var(--text-3)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: 130,
                }}
                title={n.owner}
              >
                {n.owner}
              </div>
            ) : null}
            <div style={{ fontSize: 10.5, color: "var(--text-mute)" }}>
              {n.date}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StepRowDesign({
  step,
  onOpen,
  onRunAgent,
  runAgentPending,
  onMatchAgent,
}: {
  step: StepRow;
  onOpen: () => void;
  onRunAgent: () => void;
  runAgentPending: boolean;
  onMatchAgent?: () => void;
}) {
  const s = step;
  const stateClass = s.state === "awaiting_review" ? "review" : s.state;
  const marker = s.state === "completed" ? "✓" : String(s.ord);
  const completedDate = s.completedAt
    ? new Date(s.completedAt).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      })
    : "";
  return (
    <div
      className={`step-row ${stateClass}`}
      data-testid={`step-row-${s.id}`}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter") onOpen();
      }}
    >
      <div className="step-marker">{marker}</div>
      <div className="step-body">
        <div className="top">
          <span className="ttl">{s.title}</span>
          <span className="type">{s.type}</span>
        </div>
        <div className="meta">
          <span>{s.displayOwner}</span>
          {s.isAutonomous && s.agentName ? (
            <span className="attr-badge">
              <span className="glyph">{stepTypeIcon(s.type)}</span>
              {s.agentName} · autonomous
            </span>
          ) : null}
          {s.isAutonomous && !s.agentName && onMatchAgent ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onMatchAgent(); }}
              style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                padding: "2px 8px", borderRadius: 99, border: "1px dashed var(--accent, #6366f1)",
                background: "color-mix(in srgb, var(--accent, #6366f1) 8%, transparent)",
                color: "var(--accent, #6366f1)", fontSize: 11, fontWeight: 600,
                cursor: "pointer", animation: "pulse 2s infinite",
              }}
            >
              ⚡ Match agent
            </button>
          ) : null}
        </div>
      </div>
      <div className="step-actions" onClick={(e) => e.stopPropagation()}>
        {s.state === "awaiting_review" ? (
          <>
            <button type="button" className="btn primary" onClick={onOpen}>
              Approve
            </button>
            <button type="button" className="btn" onClick={onOpen}>
              Request changes
            </button>
          </>
        ) : s.state === "running" ? (
          <>
            <span className="pill green">● running</span>
            <button type="button" className="btn" onClick={onOpen}>
              Open ↗
            </button>
          </>
        ) : s.state === "ready" ? (
          <>
            {s.isAutonomous && s.agentBindingId ? (
              <button
                type="button"
                className="btn primary"
                data-testid={`run-agent-${s.id}`}
                disabled={runAgentPending}
                onClick={onRunAgent}
                title={`Dispatch ${s.displayOwner} now (live HMAC-signed POST)`}
              >
                {runAgentPending ? "Dispatching…" : "⚡ Run agent"}
              </button>
            ) : (
              <button type="button" className="btn primary" onClick={onOpen}>
                Claim
              </button>
            )}
          </>
        ) : s.state === "pending" ? (
          s.isAutonomous && s.agentBindingId ? (
            <button
              type="button"
              className="btn"
              data-testid={`run-agent-${s.id}`}
              disabled={runAgentPending}
              onClick={onRunAgent}
              title={`Dispatch ${s.displayOwner} now (live HMAC-signed POST)`}
            >
              {runAgentPending ? "Dispatching…" : "⚡ Run agent"}
            </button>
          ) : (
            <span className="pill gray">Waiting on deps</span>
          )
        ) : s.state === "failed" ? (
          <>
            <span className="pill red">failed</span>
            {s.isAutonomous && s.agentBindingId ? (
              <button
                type="button"
                className="btn"
                data-testid={`run-agent-${s.id}`}
                disabled={runAgentPending}
                onClick={onRunAgent}
              >
                {runAgentPending ? "Dispatching…" : "⚡ Retry"}
              </button>
            ) : null}
          </>
        ) : s.state === "completed" ? (
          <span className="pill gray">
            Done{completedDate ? ` · ${completedDate}` : ""}
          </span>
        ) : (
          <span className="pill gray">{s.state.replace("_", " ")}</span>
        )}
      </div>
    </div>
  );
}

function StepDrawer({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      data-testid="step-drawer"
      role="dialog"
      aria-modal="true"
      aria-label={`Step detail: ${title}`}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 16, 22, 0.32)",
        zIndex: 900,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(560px, 92vw)",
          background: "var(--bg)",
          boxShadow: "-12px 0 32px rgba(0,0,0,0.18)",
          overflow: "auto",
        }}
      >
        <div
          style={{
            position: "sticky",
            top: 0,
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "12px 16px",
            borderBottom: "1px solid var(--border)",
            background: "var(--bg)",
            zIndex: 1,
          }}
        >
          <div style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{title}</div>
          <button
            type="button"
            className="btn"
            data-testid="step-drawer-close"
            onClick={onClose}
            style={{ fontSize: 11, padding: "4px 10px" }}
          >
            Close
          </button>
        </div>
        <div style={{ padding: 16 }}>{children}</div>
      </div>
    </div>
  );
}

function RefinePanel({
  workspaceId,
  playId,
  currentSteps,
}: {
  workspaceId: string;
  playId: string;
  currentSteps: StepRow[];
}) {
  const [input, setInput] = useState("");
  const [history, setHistory] = useState<ChatTurn[]>([]);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [proposed, setProposed] = useState<RefineResult | null>(null);
  const refine = useRefinePlay(workspaceId, playId);
  const replaceSteps = useReplaceSteps(workspaceId, playId);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const send = async () => {
    const msg = input.trim();
    if (!msg || pending) return;
    setInput("");
    setError(null);
    const nextHistory: ChatTurn[] = [...history, { id: crypto.randomUUID(), role: "user", content: msg }];
    setHistory(nextHistory);
    setPending(true);
    try {
      const result = await refine.mutateAsync({ message: msg, history: history });
      if (mounted.current) {
        setHistory([...nextHistory, { id: crypto.randomUUID(), role: "assistant", content: result.reply }]);
        if (result.proposedSteps?.length) {
          setProposed({
            ...result,
            proposedSteps: result.proposedSteps.map((s) => ({ ...s, id: crypto.randomUUID() })),
          });
        }
      }
    } catch (e) {
      if (mounted.current) setError((e as Error).message);
    } finally {
      if (mounted.current) setPending(false);
    }
  };

  const applyProposed = async () => {
    if (!proposed?.proposedSteps) return;
    try {
      // Strip the frontend-only `id` field before sending to the API.
      const stepsPayload = proposed.proposedSteps.map(({ id: _id, ...rest }) => rest);
      await replaceSteps.mutateAsync({ steps: stepsPayload as ProposedStep[] });
      if (mounted.current) {
        setHistory((h) => [
          ...h,
          { id: crypto.randomUUID(), role: "assistant", content: `Done — replaced step list with ${proposed.proposedSteps!.length} steps. The Steps tab shows the updated plan.` },
        ]);
        setProposed(null);
      }
    } catch (e) {
      if (mounted.current) setError((e as Error).message);
    }
  };

  const inputStyle: React.CSSProperties = {
    border: "1px solid var(--border)",
    borderRadius: 8,
    padding: "8px 12px",
    font: "inherit",
    fontSize: 13,
    width: "100%",
    boxSizing: "border-box",
    resize: "none",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: 720 }}>
      <p style={{ fontSize: 13, color: "var(--text-2)", margin: 0 }}>
        Chat with XAMI to refine this draft before approving. Ask questions, flag concerns, or request structural changes — XAMI can propose a revised step list for you to review.
      </p>

      {/* Transcript */}
      {history.length > 0 && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            maxHeight: 420,
            overflowY: "auto",
            padding: "12px 0",
          }}
        >
          {history.map((turn) => (
            <div
              key={turn.id}
              style={{
                alignSelf: turn.role === "user" ? "flex-end" : "flex-start",
                maxWidth: "85%",
                background: turn.role === "user" ? "var(--accent)" : "var(--surface-2)",
                color: turn.role === "user" ? "white" : "var(--text)",
                borderRadius: turn.role === "user" ? "12px 12px 2px 12px" : "12px 12px 12px 2px",
                padding: "8px 12px",
                fontSize: 13,
                lineHeight: 1.5,
                whiteSpace: turn.role === "assistant" && hasMarkdown(turn.content) ? undefined : "pre-wrap",
              }}
            >
              {turn.role === "assistant" && hasMarkdown(turn.content) ? (
                <MarkdownText>{turn.content}</MarkdownText>
              ) : (
                turn.content
              )}
            </div>
          ))}
          {pending && (
            <div style={{ alignSelf: "flex-start", fontSize: 12, color: "var(--text-3)", padding: "4px 8px" }}>
              XAMI is thinking…
            </div>
          )}
        </div>
      )}

      {/* Proposed steps preview */}
      {proposed?.proposedSteps && (
        <div
          style={{
            border: "1px solid var(--accent)",
            borderRadius: 10,
            padding: 16,
            background: "var(--surface)",
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--accent)", marginBottom: 8 }}>
            XAMI PROPOSED CHANGES ({proposed.proposedSteps.length} steps)
            {proposed.rationale ? <span style={{ fontWeight: 400, color: "var(--text-2)", marginLeft: 8 }}>{proposed.rationale}</span> : null}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 12 }}>
            {proposed.proposedSteps.map((s: ProposedStep, i: number) => (
              <div key={s.id} style={{ fontSize: 13, display: "flex", gap: 8, alignItems: "flex-start" }}>
                <span style={{ color: "var(--text-3)", minWidth: 20, fontSize: 11 }}>{i + 1}.</span>
                <div>
                  <span style={{ fontWeight: 500 }}>{s.title}</span>
                  {s.description && <span style={{ color: "var(--text-2)", marginLeft: 6 }}>— {s.description}</span>}
                  {s.requiresApproval && <span className="pill gray" style={{ fontSize: 10, marginLeft: 6 }}>signoff</span>}
                </div>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              className="btn primary"
              onClick={applyProposed}
              disabled={replaceSteps.isPending}
              data-testid="refine-apply-btn"
            >
              {replaceSteps.isPending ? "Applying…" : "Apply these steps"}
            </button>
            <button type="button" className="btn" onClick={() => setProposed(null)}>
              Discard
            </button>
          </div>
        </div>
      )}

      {/* Current steps sidebar (collapsed list for context) */}
      {history.length === 0 && currentSteps.length > 0 && (
        <div style={{ fontSize: 12, color: "var(--text-2)", border: "1px solid var(--border)", borderRadius: 8, padding: 10 }}>
          <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 11, color: "var(--text-3)" }}>CURRENT STEPS</div>
          {currentSteps.map((s) => (
            <div key={s.id} style={{ padding: "2px 0" }}>{s.ord}. {s.title}</div>
          ))}
        </div>
      )}

      {error && <div style={{ color: "crimson", fontSize: 12 }}>{error}</div>}

      {/* Input */}
      <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
          placeholder="Ask XAMI to review, adjust, or explain the plan…"
          rows={2}
          style={inputStyle}
          disabled={pending}
          data-testid="refine-input"
        />
        <button
          type="button"
          className="btn primary"
          onClick={() => void send()}
          disabled={pending || !input.trim()}
          style={{ whiteSpace: "nowrap", flexShrink: 0 }}
          data-testid="refine-send-btn"
        >
          {pending ? "…" : "Send"}
        </button>
      </div>
      <div style={{ fontSize: 11, color: "var(--text-3)" }}>Enter to send · Shift+Enter for new line</div>
    </div>
  );
}

function PlayWorkspaceInner() {
  const { workspaceId = "", playId = "" } = useParams<{
    workspaceId: string;
    playId: string;
  }>();
  const navigate = useNavigate();
  const me = useMe();
  const persona = usePersona(workspaceId);
  const query = usePlay(workspaceId, playId);
  const mutations = usePlayMutations(workspaceId, playId);
  const runAgent = useRunAgent(workspaceId, playId);

  const [tab, setTab] = useState<Tab>("steps");
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const [showReplanModal, setShowReplanModal] = useState(false);
  const [showRecurrenceModal, setShowRecurrenceModal] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmActionState | null>(null);
  const [matchingStepId, setMatchingStepId] = useState<string | null>(null);

  const onEvent = useCallback((e: WorkspaceEvent) => {
    setActivity((prev) => [eventToActivity(e, prev.length), ...prev].slice(0, 50));
  }, []);
  const { connectionStatus } = useWorkspaceEvents(workspaceId, onEvent);

  const detail = query.data;
  const steps: StepRow[] = detail?.steps ?? [];
  const events: StepEventRow[] = detail?.recentEvents ?? [];
  const pendingReplan = detail ? readPendingReplan(detail) : null;
  const plannerStore = usePlannerStore();

  const handleEdit = useCallback(() => {
    if (!detail) return;
    const { play, steps: s, dependencies } = detail;
    const entryId = play.roadmapEntryId ?? play.id;
    const edges = dependencies.map((d) => ({
      from: d.upstreamStepId,
      to: d.downstreamStepId,
    }));
    const projected = projectDraft({ id: play.id, steps: s, edges }, { initiativeTitle: play.archetype });
    plannerStore.patch(entryId, { stage: "editor", draftPlay: projected, editorView: "timeline", showDeps: true });
    navigate(`/workspaces/${workspaceId}/planner?roadmapEntryId=${encodeURIComponent(entryId)}&playId=${encodeURIComponent(play.id)}`);
  }, [detail, plannerStore, navigate, workspaceId]);

  const activeStep: StepRow | undefined = useMemo(() => {
    if (!steps.length) return undefined;
    if (selectedStepId) {
      const s = steps.find((x) => x.id === selectedStepId);
      if (s) return s;
    }
    return (
      steps.find((s) => s.state === "awaiting_review") ??
      steps.find((s) => s.state === "running") ??
      steps.find((s) => s.state === "ready") ??
      steps[0]
    );
  }, [steps, selectedStepId]);

  if (query.isLoading) {
    return (
      <div className="main-inner" data-testid="play-loading">
        Loading play…
      </div>
    );
  }
  if (query.isError || !detail) {
    return (
      <div className="main-inner" data-testid="play-error">
        Couldn’t load play: {query.error?.message ?? "unknown"}
      </div>
    );
  }

  const { play } = detail;
  const stepsDone = steps.filter((s) => s.state === "completed").length;
  const stepsTotal = steps.length;
  const elapsed = elapsedLabel(play);
  const window_ = windowLabel(play);

  const runConfirmedAction = async (state: ConfirmActionState, rationale?: string) => {
    try {
      switch (state.kind) {
        case "approve":
          await mutations.approve.mutateAsync();
          break;
        case "start":
          await mutations.start.mutateAsync();
          break;
        case "resume":
          await mutations.resume.mutateAsync();
          break;
        case "pause":
          await mutations.pause.mutateAsync(rationale ?? "");
          break;
        case "abandon":
          await mutations.abandon.mutateAsync(rationale ?? "");
          break;
      }
    } finally {
      setConfirm(null);
    }
  };

  return (
    <div
      className="main-inner"
      style={{ maxWidth: 1180 }}
      data-testid="play-workspace"
    >
      <OutcomeHeader
        outcomeStatement={play.outcomeStatement}
        successMetrics={play.successMetrics}
      />
      <div className="pw-head">
        <div>
          <div className="crumb">
            <a
              data-testid="play-back"
              onClick={() => navigate(`/workspaces/${workspaceId}/plays`)}
              style={{ cursor: "pointer" }}
            >
              Plays
            </a>
            {" / "}
            <span>{play.archetype}</span>
          </div>
          <h1 data-testid="play-title">{play.archetype}</h1>
          <div className="tags">
            <span className="archetype">{play.archetype}</span>
            <span
              className={`pill ${statusPill(play.state)}`}
              data-testid="play-state-pill"
            >
              {play.state}
            </span>
            <span
              className="pill purple"
              data-testid="play-replan-count"
            >
              ⟲ {play.replanCount} replans
            </span>
            {/* Source badge — only renders for non-XI sources so the
                XI-promoted default stays uncluttered. */}
            {query.data?.lineageSource === "template_direct" ? (
              <span
                className="pill"
                style={{
                  background: "color-mix(in srgb, var(--accent, #6366f1) 12%, transparent)",
                  color: "var(--accent, #6366f1)",
                }}
                title="Created directly from a template — no XI signal lineage. Outcomes don't write back to XI."
                data-testid="play-source-template"
              >
                ✦ Template
              </span>
            ) : null}
            {play.campaignId ? (
              <span
                className="pill"
                style={{
                  background: "color-mix(in srgb, var(--accent, #6366f1) 12%, transparent)",
                  color: "var(--accent, #6366f1)",
                  cursor: "pointer",
                }}
                title="Part of a GamePlan — click to open"
                data-testid="campaign-badge"
                onClick={() =>
                  navigate(
                    `/workspaces/${workspaceId}/gameplans/${play.campaignId}`,
                  )
                }
              >
                ⛓ GamePlan: {play.campaignTitle ?? "open"}
              </span>
            ) : null}
            <span className="pill">Owner: {play.ownerUserId}</span>
          </div>
        </div>
        <div className="pw-stats">
          <div className="item">
            <div className="v" data-testid="play-steps-stat">
              {stepsDone}/{stepsTotal}
            </div>
            <div className="l">Steps</div>
          </div>
          <div className="item">
            <div className="v">{elapsed}</div>
            <div className="l">Elapsed</div>
          </div>
          <div className="item">
            <div className="v">{window_}</div>
            <div className="l">Window</div>
          </div>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          padding: "8px 0",
          borderBottom: "1px solid var(--border)",
          marginBottom: 12,
        }}
      >
        <CxoActions
          state={play.state}
          persona={persona}
          onApprove={() => setConfirm({ kind: "approve", needsRationale: false })}
          onStart={() => setConfirm({ kind: "start", needsRationale: false })}
          onPause={() => setConfirm({ kind: "pause", needsRationale: true })}
          onResume={() => setConfirm({ kind: "resume", needsRationale: false })}
          onAbandon={() => setConfirm({ kind: "abandon", needsRationale: true })}
          onRequestReplan={() => setShowReplanModal(true)}
          onEdit={handleEdit}
          onMakeRecurring={() => setShowRecurrenceModal(true)}
          hasRoadmapEntry={!!play.roadmapEntryId}
          pending={
            mutations.approve.isPending ||
            mutations.start.isPending ||
            mutations.pause.isPending ||
            mutations.resume.isPending ||
            mutations.abandon.isPending
          }
        />
      </div>

      <div className="pw-tabs">
        {(["steps", "timeline", "outcome", "governance", ...(play.state === "draft" ? ["refine"] : [])] as Tab[]).map((k) => (
          <button
            key={k}
            type="button"
            className={`pw-tab ${tab === k ? "active" : ""}`}
            onClick={() => setTab(k)}
            data-testid={`play-tab-${k}`}
          >
            {k === "steps"
              ? "Steps"
              : k === "timeline"
                ? "Timeline"
                : k === "outcome"
                  ? "Outcome"
                  : k === "refine"
                    ? "✦ Refine with XAMI"
                    : "Governance"}
            {k === "steps" ? (
              <span className="badge">{stepsTotal}</span>
            ) : null}
            {k === "governance" ? (
              <span className="badge">{play.replanCount}</span>
            ) : null}
          </button>
        ))}
      </div>

      <div className="pw-grid">
        <div data-testid="play-main">
          {tab === "steps" ? (
            <div className="steps" data-testid="step-list">
              {pendingReplan ? (
                <>
                  <div className="replan-banner" data-testid="play-replan-banner">
                    <div className="icon">⟲</div>
                    <div>
                      <div className="ttl">Replan ready — review needed</div>
                      <div className="meta">
                        {pendingReplan.triggerKind.replace(/_/g, " ")}
                      </div>
                    </div>
                  </div>
                  <ReplanCard
                    replan={pendingReplan}
                    workspaceId={workspaceId}
                    playId={playId}
                    persona={persona}
                  />
                </>
              ) : null}
              {steps.map((s) => (
                <StepRowDesign
                  key={s.id}
                  step={s}
                  onOpen={() => setSelectedStepId(s.id)}
                  onRunAgent={() => runAgent.mutate(s.id)}
                  runAgentPending={
                    runAgent.isPending && runAgent.variables === s.id
                  }
                  {...(s.isAutonomous && !s.agentName ? { onMatchAgent: () => setMatchingStepId(s.id) } : {})}
                />
              ))}
            </div>
          ) : null}

          {tab === "timeline" ? (
            <div data-testid="play-timeline">
              <PlayTimeline detail={detail} />
            </div>
          ) : null}

          {tab === "outcome" ? (
            <div
              data-testid="play-outcome"
              style={{
                fontSize: 13,
                color: "var(--text-3)",
                padding: 20,
                textAlign: "center",
              }}
            >
              Outcome panel — primary post-completion. Currently in run; live
              KPIs visible in side panel.
            </div>
          ) : null}

          {tab === "governance" ? (
            <GovernancePanel
              workspaceId={workspaceId}
              playId={playId}
              successMetrics={play.successMetrics ?? []}
            />
          ) : null}

          {tab === "refine" && play.state === "draft" ? (
            <div data-testid="play-refine" style={{ padding: "16px 0" }}>
              <RefinePanel
                workspaceId={workspaceId}
                playId={playId}
                currentSteps={steps}
              />
            </div>
          ) : null}
        </div>

        <div className="pw-side" data-testid="play-side">
          <div className="side-card" data-testid="live-outcome-card">
            <div className="h">
              <span>Live outcome</span>
              <span className="live" />
              <span className="right">daily capture</span>
            </div>
            <div className="outcome-card">
              <div className="lbl">Progress</div>
              <div className="row">
                <span className="v green">
                  {stepsDone}/{stepsTotal} steps
                </span>
                <span className="arr">
                  {stepsTotal === 0
                    ? "—"
                    : `${Math.round((stepsDone / stepsTotal) * 100)}% complete`}
                </span>
              </div>
              <div className="bar">
                <span
                  style={{
                    width:
                      stepsTotal === 0
                        ? "0%"
                        : `${Math.round((stepsDone / stepsTotal) * 100)}%`,
                  }}
                />
              </div>
              <div className="lo">
                <span>start</span>
                <span>target {window_}</span>
              </div>
            </div>
          </div>

          <div className="side-card" data-testid="activity-rail">
            <div className="h">
              <span>Activity</span>
              <span
                className="live"
                style={{
                  background:
                    connectionStatus === "open"
                      ? "var(--accent-2)"
                      : "var(--text-mute)",
                }}
              />
              <span className="right">last 24h</span>
            </div>
            {activity.length === 0 && events.length === 0 ? (
              <div
                className="side-act"
                data-testid="activity-empty"
                style={{ color: "var(--text-3)" }}
              >
                No activity yet.
              </div>
            ) : (
              <>
                {activity.map((a) => (
                  <div
                    key={a.id}
                    className="side-act entering"
                    data-testid={`activity-${a.id}`}
                  >
                    <div className="time">{a.time}</div>
                    <div>
                      <span className="who">{a.who}</span> {a.text}
                    </div>
                  </div>
                ))}
                {events.slice(0, 10).map((e) => (
                  <div
                    key={e.id}
                    className="side-act"
                    data-testid={`event-${e.id}`}
                  >
                    <div className="time">
                      {new Date(e.occurredAt).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                    <div>
                      <span className="who">{e.actorKind}</span>{" "}
                      {e.eventType}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </div>

      {selectedStepId && activeStep ? (
        <StepDrawer
          onClose={() => setSelectedStepId(null)}
          title={activeStep.title}
        >
          {pendingReplan ? (
            <ReplanCard
              replan={pendingReplan}
              workspaceId={workspaceId}
              playId={playId}
              persona={persona}
            />
          ) : (
            <StepDetail
              step={activeStep}
              play={play}
              workspaceId={workspaceId}
              currentUserId={me.data?.user.id ?? ""}
              persona={persona}
            />
          )}
        </StepDrawer>
      ) : null}

      {showReplanModal ? (
        <RequestReplanModal
          workspaceId={workspaceId}
          playId={playId}
          onClose={() => setShowReplanModal(false)}
        />
      ) : null}

      {showRecurrenceModal ? (
        <PlayRecurrenceModal
          workspaceId={workspaceId}
          entryId={play.roadmapEntryId}
          playId={playId}
          playTitle={play.archetype}
          onClose={() => setShowRecurrenceModal(false)}
        />
      ) : null}

      {matchingStepId ? (() => {
        const matchStep = steps.find((s) => s.id === matchingStepId);
        return matchStep ? (
          <AgentMatchModal
            stepId={matchingStepId}
            stepTitle={matchStep.title}
            stepType={matchStep.type}
            workspaceId={workspaceId}
            onClose={() => setMatchingStepId(null)}
            onAssigned={() => { query.refetch(); setMatchingStepId(null); }}
          />
        ) : null;
      })() : null}

      {confirm ? (
        <ConfirmActionModal
          state={confirm}
          onCancel={() => setConfirm(null)}
          onConfirm={(rationale) => runConfirmedAction(confirm, rationale)}
        />
      ) : null}
    </div>
  );
}

interface ConfirmActionModalProps {
  state: ConfirmActionState;
  onCancel: () => void;
  onConfirm: (rationale?: string) => void;
}

function ConfirmActionModal({
  state,
  onCancel,
  onConfirm,
}: ConfirmActionModalProps) {
  const [rationale, setRationale] = useState("");
  const label = {
    approve: "Approve play?",
    start: "Start play?",
    pause: "Pause play?",
    resume: "Resume play?",
    abandon: "Abandon play?",
  }[state.kind];
  return (
    <div
      data-testid="confirm-action-modal"
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: 8,
          padding: 24,
          width: "min(420px, 92vw)",
          boxShadow: "0 12px 48px rgba(0,0,0,0.18)",
        }}
      >
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>{label}</h3>
        {state.needsRationale ? (
          <textarea
            data-testid="confirm-rationale"
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            rows={3}
            placeholder="Rationale (required)"
            style={{
              marginTop: 12,
              width: "100%",
              border: "1px solid var(--border)",
              borderRadius: 6,
              padding: 8,
              fontFamily: "inherit",
            }}
          />
        ) : null}
        <div
          style={{
            display: "flex",
            gap: 8,
            justifyContent: "flex-end",
            marginTop: 16,
          }}
        >
          <button
            type="button"
            className="btn"
            onClick={onCancel}
            data-testid="confirm-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn primary"
            data-testid="confirm-submit"
            disabled={state.needsRationale && !rationale.trim()}
            onClick={() =>
              onConfirm(state.needsRationale ? rationale.trim() : undefined)
            }
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

function PlayWorkspaceErrorFallback({
  error,
  resetErrorBoundary,
}: {
  error: Error;
  resetErrorBoundary: () => void;
}) {
  return (
    <div className="main-inner" data-testid="play-workspace-error">
      <h1>Play unavailable</h1>
      <div style={{ fontSize: 13, color: "var(--text-3)" }}>
        {error.message}
      </div>
      <button
        type="button"
        className="btn primary"
        style={{ marginTop: 12 }}
        onClick={resetErrorBoundary}
      >
        Reload surface
      </button>
    </div>
  );
}

export function PlayWorkspace() {
  return (
    <ErrorBoundary FallbackComponent={PlayWorkspaceErrorFallback}>
      <PlayWorkspaceInner />
    </ErrorBoundary>
  );
}
